import { useState, useEffect, useCallback } from "react";
import { getMonthlyReport } from "../services/analyticsService";

/**
 * Custom hook for fetching monthly report data
 * @param {string|number} tenantId - Tenant ID
 * @param {number|null} year - Year (null for current)
 * @param {number|null} month - Month (null for current)
 * @returns {Object} { data, loading, error, refresh }
 */
export const useMonthlyReport = (tenantId, year, month) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    // If year/month is null, don't fetch (will use current analytics)
    if (year === null || month === null) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getMonthlyReport(tenantId, year, month);
      
      if (response?.success) {
        if (response.data) {
          setData(response.data);
        } else {
          setError(new Error(response.message || "No records found for this month"));
          setData(null);
        }
      } else {
        setError(new Error("Failed to load monthly report"));
        setData(null);
      }
    } catch (err) {
      console.error("Error fetching monthly report:", err);
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId, year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh };
};
